<template>
  <div class="content-main relative">
    <div class="content">
      <div class="header flex flex-col">
        <h1 class="p-2 text-xl font-bold">Orders distribution</h1>
      </div>
      <div class="p-2">
        <div class="flex flex-row flex-shrink-1 flex-wrap">
          <div class="flex flex-col px-2 mb-4">
            <label for="from-date">
              From date
            </label>
            <input
              type="date"
              class="regular bordered full"
              name="from-date"
              id="from-date"
              v-model="fromDate"
            />
          </div>
          <div class="flex flex-col px-2 mb-4">
            <label for="to-date">
              To date
            </label>
            <input
              type="date"
              class="regular bordered full"
              name="to-date"
              id="to-date"
              v-model="toDate"
            />
          </div>
          <div class="flex flex-col px-2 mb-4">
            <label for="departamento">
              Departamento
            </label>
            <select
              id="departamento"
              class="default-select bordered"
              v-model="departamentoId"
            >
              <option value="">All departments</option>
              <option
                v-for="dept in departments"
                :value="dept.value"
                :key="dept.value"
              >
                {{ dept.label }}
              </option>
            </select>
          </div>
          <div class="flex flex-col px-2 mb-4">
            <label for="to-date">
              Group by postal code
            </label>
            <input
              id="select-all-cb"
              class="my-5"
              @click.stop
              type="checkbox"
              v-model="grouped"
            />
          </div>
          <div v-if="paramsChanged" class="flex ml-4">
            <button
              @click="getOrders"
              class="btn btn-info-button px-4 mt-auto mb-6"
            >
              Filter now
            </button>
          </div>
          <div class="flex ml-4">
            <button
              @click="startGeodecode()"
              :disabled="geodecodeLoading"
              class="btn btn-info-button px-4 mt-auto mb-6"
            >
              {{ geodecodeLoading ? "Processing..." : "Run Geodecode" }}
            </button>
            <label class="flex items-center ml-4 mt-auto mb-6 gap-1">
              <input type="checkbox" v-model="geodecodeAutoFetch" />
              Auto-continue
            </label>
          </div>
        </div>
        <div v-if="geodecodeStats" class="p-2 mb-2 border rounded bg-gray-50">
          <div class="flex gap-4 flex-wrap">
            <div>
              Batch: <strong>{{ geodecodeStats.batch }}</strong>
            </div>
            <div>
              Success: <strong>{{ geodecodeStats.totalSuccess }}</strong>
            </div>
            <div>
              Errors: <strong>{{ geodecodeStats.totalErrors }}</strong>
            </div>
            <div>
              Already geocoded:
              <strong>{{ geodecodeStats.totalAlreadyGeocoded }}</strong>
            </div>
            <div>
              Pending: <strong>{{ geodecodeStats.pending }}</strong>
            </div>
          </div>
          <div
            v-if="geodecodeStats.pending > 0 && geodecodeLoading"
            class="mt-1 text-sm text-gray-500"
          >
            Auto-continuing... batch {{ geodecodeStats.batch }}
          </div>
          <div
            v-if="geodecodeStats.pending === 0"
            class="mt-1 text-sm text-green-600 font-bold"
          >
            Done! All addresses geocoded.
          </div>
        </div>
      </div>
      <div class="flex gap-2 row">
        <div>Total orders: {{ total }}</div>
        <div class="border-r"></div>
        <div>Total orders with location: {{ totalWithLocation }}</div>
      </div>
      <div class="row">
        <div class="card-body">
          <div v-if="loading" class="w-20 h-20 mx-auto">
            <BaseMiniSpinner v-if="loading"></BaseMiniSpinner>
          </div>
          <div v-else-if="!orders">
            No orders
          </div>
          <div ref="map" v-if="!loading && orders" style="height: 800px;"></div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import {
  getOrderSyncStats,
  getStatsGeodecode
} from "@/api/store/NkrsyncStats.js";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Loader } from "@googlemaps/js-api-loader";

export default {
  data() {
    return {
      orders: null,
      total: 0,
      totalWithLocation: 0,
      loading: true,
      geodecodeLoading: false,
      geodecodeStats: null,
      geodecodeAutoFetch: true,
      markers: [],
      map: null,
      fromDate: this.formatDate(
        new Date(new Date().setMonth(new Date().getMonth() - 6))
      ),
      toDate: this.formatDate(new Date()),
      grouped: false,
      departamentoId: "",
      params: ""
    };
  },
  async mounted() {
    await this.getOrders();
  },
  computed: {
    departments() {
      return this.$store.getters["general/getDepartments"].map(d => ({
        label: d.name,
        value: d.full.departamentoId
      }));
    },
    paramsChanged() {
      return (
        this.params !==
        `from=${this.fromDate}&to=${this.toDate}&grouped=${this.grouped}&dept=${this.departamentoId}`
      );
    }
  },
  methods: {
    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    initMap() {
      const loader = new Loader({
        apiKey: process.env.VUE_APP_GOOGLE_MAPS_API_KEY,
        version: "weekly"
      });
      loader.load().then(() => {
        const google = window.google;
        this.map = new google.maps.Map(this.$refs.map, {
          center: { lat: 25.814794, lng: -80.323547 }, // Example coordinates (New York City)
          zoom: 3
        });
        this.$nextTick(() => {
          const markers = this.orders?.map(order => {
            return new google.maps.Marker({
              position: { lat: Number(order.lat), lng: Number(order.lng) },
              map: this.map
            });
          });
          console.log(markers);
          new MarkerClusterer({ map: this.map, markers: markers });
        });
      });
    },
    startGeodecode(limit = 500) {
      this.geodecodeStats = {
        batch: 0,
        totalSuccess: 0,
        totalErrors: 0,
        totalAlreadyGeocoded: 0,
        pending: "..."
      };
      this.runGeodecodeLoop(limit);
    },
    async runGeodecodeLoop(limit = 500) {
      this.geodecodeLoading = true;
      while (this.geodecodeAutoFetch) {
        try {
          const response = await getStatsGeodecode(limit);
          this.geodecodeStats.batch++;
          this.geodecodeStats.totalSuccess += response.success;
          this.geodecodeStats.totalErrors += response.errors;
          this.geodecodeStats.totalAlreadyGeocoded += response.alreadyGeocoded;
          this.geodecodeStats.pending = Number(response.totalPendingToGeocode);
          if (
            this.geodecodeStats.pending === 0 ||
            response.errors >= response.success + response.alreadyGeocoded
          ) {
            const msg =
              this.geodecodeStats.pending === 0
                ? "Geodecode complete! All addresses processed."
                : `Geodecode stopped: only errors remaining (${this.geodecodeStats.pending} pending)`;
            this.$toast.open({
              message: msg,
              type: this.geodecodeStats.pending === 0 ? "success" : "warning",
              position: "top-right"
            });
            break;
          }
        } catch {
          this.$toast.open({
            message: "Error running geodecode, stopped.",
            type: "error",
            position: "top-right"
          });
          break;
        }
      }
      this.geodecodeLoading = false;
    },
    async getOrders() {
      this.loading = true;
      this.params = `from=${this.fromDate}&to=${this.toDate}&grouped=${this.grouped}&dept=${this.departamentoId}`;
      await getOrderSyncStats(this.fromDate, this.toDate, this.departamentoId)
        .then(response => {
          this.total = response.total;
          this.totalWithLocation = response.totalWithLocation;
          this.orders = response.data;
        })
        .catch(() => {
          this.loading = false;
          this.$toast.open({
            message: `Error Loading orders`,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.loading = false;
          this.initMap();
        });
    }
  }
};
</script>
